import * as React from "react";
import { styled } from "styled-components";
import { Button, Form, Modal, Stack } from "react-bootstrap";
import { AnyTaggedItem } from "src/types";
import { TaskContentEditor } from "src/components/TaskContentEditor";
import { useBackButton } from "src/hooks/useBackButton";
import { BriefcaseIcon, ClockIcon, KeyIcon, LucideIcon, TargetIcon, XIcon } from "lucide-react";
import { TaskFormPill } from "./TaskFormPill";
import { useLiveQuery } from "dexie-react-hooks";
import { getGoals } from "src/database/queries/goals";
import type { TaskFormContext } from "./taskFormContext";
import { Habit, Task, TaskComponentType } from "@iterate/common/types/domain";

interface Props {
  form: TaskFormContext;
}

interface TaskPillProps<T extends AnyTaggedItem> {
  item: T;
  setField: <K extends keyof T>(item: T, key: K, value: T[K]) => void;
}

interface TaskPill<T extends AnyTaggedItem> {
  label: string;
  icon: LucideIcon;
  type: TaskComponentType;
  only?: T["type"][];
  /** When this returns null, the active pill shows no section below (removal stays on the pill row). */
  render: React.ComponentType<TaskPillProps<T>>;
}

const TASK_PILLS: TaskPill<any>[] = [
  {
    label: "Timebox",
    icon: ClockIcon,
    type: "timebox",
    render({ item, setField }) {
      return (
        <Stack direction="horizontal" gap={2} className="mb-3">
          <Form.Control
            id="task-starttime"
            size="sm"
            placeholder="Start Time"
            value={item.startTime ?? ""}
            autoFocus
            onChange={(e) => setField(item, "startTime", e.target.value as any)}
          />
          <Form.Control
            id="task-endtime"
            size="sm"
            placeholder="End Time"
            value={item.endTime ?? ""}
            onChange={(e) => setField(item, "endTime", e.target.value as any)}
          />
        </Stack>
      );
    },
  },
  {
    label: "Condition",
    icon: KeyIcon,
    type: "condition",
    render({ item, setField }) {
      return (
        <Form.Group className="mb-3">
          <Form.Control
            id="task-condition"
            size="sm"
            placeholder="Condition"
            value={item.condition ?? ""}
            autoFocus
            onChange={(e) => setField(item, "condition", e.target.value as any)}
          />
        </Form.Group>
      );
    },
  },
  {
    label: "Goals",
    icon: TargetIcon,
    type: "goals",
    only: ["habit", "task"],
    render({ item, setField }: TaskPillProps<Habit | Task>) {
      const goals = useLiveQuery(() => getGoals());
      return (
        <Form.Group className="mb-3">
          <Form.Select
            id="task-goal"
            size="sm"
            disabled={!goals}
            value={item.goalId ?? undefined}
            onChange={(e) =>
              setField(item, "goalId", e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">N/A</option>
            {goals?.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    },
  } as TaskPill<Habit | Task>,
  {
    label: "Project",
    icon: BriefcaseIcon,
    type: "project",
    only: ["task"],
    render() {
      return null;
    },
  } as TaskPill<Task>,
];

export function TaskForm({ form }: Props) {
  const { item } = form;
  const nameRef = React.useRef<HTMLInputElement>(null);

  const [emptyBodyPillTypes, setEmptyBodyPillTypes] = React.useState<
    ReadonlySet<TaskComponentType>
  >(() => new Set());

  const onEmptyBody = React.useCallback((type: TaskComponentType, empty: boolean) => {
    setEmptyBodyPillTypes((prev) => {
      const next = new Set(prev);
      if (empty) {
        next.add(type);
      } else {
        next.delete(type);
      }
      return next;
    });
  }, []);

  const removeComponent = React.useCallback(
    (pillType: TaskComponentType) => {
      if (!item) {
        return;
      }
      const nextComponents = item.components.filter((c) => c !== pillType);
      if (item.type === "task" && pillType === "project") {
        form.update({
          ...item,
          components: nextComponents,
          isProject: false,
        });
        return;
      }
      form.update({ ...item, components: nextComponents });
    },
    [form, item],
  );

  useBackButton(item ? form.close : null);

  if (!item) {
    return null;
  }

  const setField = <T extends AnyTaggedItem, K extends keyof T>(item: T, key: K, value: T[K]) => {
    const newItem = { ...item, [key]: value };
    form.update(newItem);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.submit();
  };

  const setFocus = () => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  };

  const addComponent = (component: TaskComponentType) => {
    if (item.type === "task" && component === "project") {
      form.update({
        ...item,
        components: [...item.components, "project"],
        isProject: true,
      });
      return;
    }
    form.update({
      ...item,
      components: [...item.components, component],
    });
  };

  return (
    <Modal show onEnter={setFocus} onHide={form.close} data-testid="task-form">
      <Form onSubmit={onSubmit}>
        <Modal.Body>
          <TaskName
            ref={nameRef}
            placeholder="Task Name"
            autoFocus
            value={item.name ?? ""}
            onChange={(e) => setField(item, "name", e.target.value)}
          />
          <TaskPills>
            {TASK_PILLS.map((pill) => {
              if (pill.only && !pill.only.includes(item.type)) {
                return null;
              }
              const isActive = item.components.includes(pill.type);
              if (isActive && emptyBodyPillTypes.has(pill.type)) {
                return (
                  <TaskFormPill
                    key={`active-${pill.type}`}
                    active
                    label={pill.label}
                    icon={pill.icon}
                    data-testid={`task-pill-${pill.type}-active`}
                    onClick={() => removeComponent(pill.type)}
                  />
                );
              }
              if (isActive) {
                return null;
              }
              return (
                <TaskFormPill
                  key={pill.type}
                  active={false}
                  label={pill.label}
                  icon={pill.icon}
                  data-testid={`task-pill-${pill.type}`}
                  onClick={() => addComponent(pill.type)}
                />
              );
            })}
          </TaskPills>
          <TaskComponents>
            {TASK_PILLS.map((pill) => {
              if (!item.components.includes(pill.type)) {
                return null;
              }
              return (
                <TaskPillActiveSection
                  key={`${item.id}-${pill.type}`}
                  pill={pill}
                  item={item}
                  setField={setField}
                  onRemove={() => removeComponent(pill.type)}
                  onEmptyBody={onEmptyBody}
                />
              );
            })}
          </TaskComponents>
          <TaskContentEditor
            value={item.content}
            data-testid="task-content-editor"
            onChange={(content) => setField(item, "content", content)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" onClick={onSubmit}>
            Save Task
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

const TaskName = styled.input.attrs({ type: "text" })`
  width: 100%;
  background: transparent;
  border: none;
  padding: 5px 0;
  outline: none;
`;

const TaskPills = styled.div`
  padding: 5px 0 10px;
  display: flex;
  gap: 5px;
  position: relative;
`;

/** Two-column grid: label column + fields; each TaskComponentSection uses `display: contents` so rows stack here. */
const TaskComponents = styled.div`
  position: relative;
  padding: 0 0 10px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: 10px;
  row-gap: 10px;
  align-items: center;
`;

const TaskComponentSection = styled.div`
  display: contents;
`;

const TaskComponentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 10px;
`;

const TaskComponentTitle = styled.div`
  font-size: 14px;
  white-space: nowrap;
`;

const TaskComponentContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  min-width: 0;
  width: 100%;

  .mb-3:last-child {
    margin-bottom: 0 !important;
  }
`;

const TaskComponentDelete = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary-color);
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    color: var(--text-primary-color);
  }
`;

/** Out of grid flow so measuring active pills does not add spurious grid rows. */
const visuallyHiddenMeasure = {
  position: "absolute" as const,
  left: 0,
  top: 0,
  width: 1,
  height: 1,
  margin: -1,
  overflow: "hidden" as const,
  clipPath: "inset(50%)",
  whiteSpace: "nowrap" as const,
  pointerEvents: "none" as const,
};

type PillBodyMode = "unknown" | "empty" | "full";

/** Measures pill body once; omits the grid section when the render output is empty. */
function TaskPillActiveSection({
  pill,
  item,
  setField,
  onRemove,
  onEmptyBody,
}: {
  pill: TaskPill<any>;
  item: AnyTaggedItem;
  setField: TaskPillProps<AnyTaggedItem>["setField"];
  onRemove: () => void;
  onEmptyBody: (type: TaskComponentType, empty: boolean) => void;
}) {
  const [mode, setMode] = React.useState<PillBodyMode>("unknown");
  const measureRef = React.useRef<HTMLDivElement>(null);
  const Component = pill.render;

  React.useLayoutEffect(() => {
    if (mode !== "unknown") {
      return;
    }
    const el = measureRef.current;
    const empty =
      !el ||
      (el.childNodes.length === 0 && (el.textContent === null || el.textContent.trim() === ""));
    setMode(empty ? "empty" : "full");
  }, [mode, item, pill.type]);

  React.useLayoutEffect(() => {
    if (mode === "unknown") {
      return;
    }
    if (mode === "empty") {
      onEmptyBody(pill.type, true);
      return () => onEmptyBody(pill.type, false);
    }
    onEmptyBody(pill.type, false);
    return () => onEmptyBody(pill.type, false);
  }, [mode, onEmptyBody, pill.type]);

  if (mode === "unknown") {
    return (
      <div ref={measureRef} style={visuallyHiddenMeasure} aria-hidden>
        <Component item={item} setField={setField} />
      </div>
    );
  }

  if (mode === "empty") {
    return null;
  }

  return (
    <TaskComponentSection>
      <TaskComponentHeader>
        <pill.icon size={12} />
        <TaskComponentTitle>{pill.label}</TaskComponentTitle>
      </TaskComponentHeader>
      <TaskComponentContent>
        <Component item={item} setField={setField} />
      </TaskComponentContent>
      <TaskComponentDelete type="button" aria-label={`Remove ${pill.label}`} onClick={onRemove}>
        <XIcon size={14} />
      </TaskComponentDelete>
    </TaskComponentSection>
  );
}
